import React, { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { cn } from '@/lib/utils'
import { API_ENDPOINTS } from '@/utils/constants'

export const LoginPage: React.FC = () => {
  const { login } = useAuth()
  // Email authentication (active)
  const [step, setStep] = useState<'email' | 'otp'>('email')
  const [email, setEmail] = useState('')
  const [otp, setOtp] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [countdown, setCountdown] = useState(0)

  // Phone authentication (commented for future use)
  // const [step, setStep] = useState<'phone' | 'otp'>('phone')
  // const [phoneNumber, setPhoneNumber] = useState('')

  // Countdown timer effect
  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | undefined
    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown(prev => prev - 1)
      }, 1000)
    }
    return () => {
      if (timer !== undefined) clearInterval(timer)
    }
  }, [countdown])

  // Email authentication (active)
  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    const trimmedEmail = email.trim().toLowerCase()

    try {
      const response = await fetch(API_ENDPOINTS.AUTH.SEND_EMAIL_OTP, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: trimmedEmail })
      })

      if (response.ok) {
        const responseData = await response.json()
        const data = responseData.data || responseData

        if (data.success) {
          setStep('otp')
          setCountdown(45) // Set initial countdown when OTP is sent
        } else if (data.cooldown) {
          setError(`Please wait ${data.cooldown} seconds before requesting another OTP`)
        } else {
          setError('Failed to send OTP. Please try again.')
        }
      } else {
        const errorResponse = await response.json()
        const errorData = errorResponse.data || errorResponse
        setError(errorData.message || 'Failed to send OTP')
      }
    } catch (err) {
      setError('Network error. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  // Phone authentication (commented for future use)
  /*
  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    const trimmedPhoneNumber = phoneNumber.trim()

    try {
      const response = await fetch(API_ENDPOINTS.AUTH.SEND_OTP, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ phoneNumber: `+91${trimmedPhoneNumber}` })
      })

      if (response.ok) {
        setStep('otp')
      } else {
        const errorResponse = await response.json()
        const errorData = errorResponse.data || errorResponse
        setError(errorData.message || 'Failed to send OTP')
      }
    } catch (err) {
      setError('Network error. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }
  */

  // Email authentication (active)
  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      const success = await login(email.trim().toLowerCase(), otp)
      if (!success) {
        setError('Invalid OTP or access denied. Only counselors can access this dashboard.')
      }
    } catch (err) {
      setError('Login failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  // Phone authentication (commented for future use)
  /*
  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      const success = await login(`+91${phoneNumber.trim()}`, otp)
      if (!success) {
        setError('Invalid OTP or access denied. Only counselors can access this dashboard.')
      }
    } catch (err) {
      setError('Login failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }
  */

  // Email authentication (active)
  const handleResendOtp = async () => {
    setIsLoading(true)
    setError('')

    try {
      const response = await fetch(API_ENDPOINTS.AUTH.SEND_EMAIL_OTP, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: email.trim().toLowerCase() })
      })

      if (response.ok) {
        const responseData = await response.json()
        const data = responseData.data || responseData

        if (data.success) {
          setCountdown(45) // Reset countdown
          setOtp('') // Clear current OTP
        } else if (data.cooldown) {
          setError(`Please wait ${data.cooldown} seconds before requesting another OTP`)
        } else {
          setError('Failed to resend OTP. Please try again.')
        }
      } else {
        const errorResponse = await response.json()
        const errorData = errorResponse.data || errorResponse
        setError(errorData.message || 'Failed to resend OTP')
      }
    } catch (err) {
      setError('Network error. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleBackToEmail = () => {
    setStep('email')
    setOtp('')
    setError('')
    setCountdown(0)
  }

  const formatCountdown = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  // Phone authentication (commented for future use)
  /*
  const handleBackToPhone = () => {
    setStep('phone')
    setOtp('')
    setError('')
  }
  */

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Counselor Dashboard</h1>
            <p className="text-gray-600">
              {step === 'email' ? 'Enter your email address to continue' : 'Enter the OTP sent to your email'}
            </p>
          </div>

          {/* Email Form (active) */}
          {step === 'email' && (
            <form onSubmit={handleSendOtp} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  required
                  className={cn(
                    "w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent",
                    "placeholder-gray-400 text-gray-900"
                  )}
                />
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading || !email.trim()}
                className={cn(
                  "w-full bg-primary text-white py-3 px-4 rounded-lg font-medium",
                  "hover:bg-primary/90 focus:ring-2 focus:ring-primary focus:ring-offset-2",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                  "transition-colors duration-200"
                )}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Sending OTP...
                  </div>
                ) : (
                  'Send OTP'
                )}
              </button>
            </form>
          )}

          {/* Phone Number Form (commented for future use) */}
          {/*
          {step === 'phone' && (
            <form onSubmit={handleSendOtp} className="space-y-6">
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <div className="flex items-center">
                  <span className="px-3 py-3 border border-gray-300 bg-gray-100 rounded-l-lg text-gray-600">+91</span>
                  <input
                    id="phone"
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="9876543210"
                    required
                    className={cn(
                      "w-full px-4 py-3 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-primary focus:border-transparent",
                      "placeholder-gray-400 text-gray-900"
                    )}
                  />
                </div>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading || !phoneNumber.trim()}
                className={cn(
                  "w-full bg-primary text-white py-3 px-4 rounded-lg font-medium",
                  "hover:bg-primary/90 focus:ring-2 focus:ring-primary focus:ring-offset-2",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                  "transition-colors duration-200"
                )}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Sending OTP...
                  </div>
                ) : (
                  'Send OTP'
                )}
              </button>
            </form>
          )}

          {/* OTP Form */}
          {step === 'otp' && (
            <form onSubmit={handleVerifyOtp} className="space-y-6">
              <div>
                <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-2">
                  Enter OTP
                </label>
                <input
                  id="otp"
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  maxLength={6}
                  required
                  className={cn(
                    "w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent",
                    "placeholder-gray-400 text-gray-900 text-center text-lg tracking-widest"
                  )}
                />
                <p className="text-xs text-gray-500 mt-1">
                  OTP sent to {email}
                </p>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <div className="space-y-3">
                <button
                  type="submit"
                  disabled={isLoading || otp.length !== 6}
                  className={cn(
                    "w-full bg-primary text-white py-3 px-4 rounded-lg font-medium",
                    "hover:bg-primary/90 focus:ring-2 focus:ring-primary focus:ring-offset-2",
                    "disabled:opacity-50 disabled:cursor-not-allowed",
                    "transition-colors duration-200"
                  )}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Verifying...
                    </div>
                  ) : (
                    'Verify & Login'
                  )}
                </button>

                {/* Resend OTP / Countdown */}
                <div className="text-center">
                  {countdown > 0 ? (
                    <p className="text-sm text-gray-500">
                      Resend OTP in {formatCountdown(countdown)}
                    </p>
                  ) : (
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={isLoading}
                      className="text-sm text-primary hover:text-primary/80 disabled:opacity-50 font-medium"
                    >
                      {isLoading ? 'Sending...' : 'Resend OTP'}
                    </button>
                  )}
                </div>

                <button
                  type="button"
                  onClick={handleBackToEmail}
                  className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-200 transition-colors duration-200"
                >
                  Back to Email Address
                </button>
              </div>
            </form>
          )}

          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="text-xs text-gray-500">
              Only authorized counselors can access this dashboard
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}